// ─────────────────────────────────────────────────────────────
// Theme Manager
//
// Each theme has two brand colours:
//   dark  → solid brand colour  (e.g. buttons, active states)
//   light → tint colour         (e.g. backgrounds, chips)
//
// CSS variables set on :root
//   --colorDark   → dark value
//   --colorLight  → light value
//
// Old --primaryDark / --primaryLight aliases in index.css
// keep existing styles working until design-system cleanup.
// ─────────────────────────────────────────────────────────────

export const THEMES = [
  { key: "orange",  name: "Orange",  dark: "#FF7300", light: "rgba(255, 115,   0, 0.10)" },
  { key: "teal",    name: "Teal",    dark: "#0CB5AD", light: "rgba(  0, 199, 190, 0.10)" },
  { key: "emerald", name: "Emerald", dark: "#0DAF53", light: "rgba( 52, 199,  89, 0.10)" },
  { key: "indigo",  name: "Indigo",  dark: "#5856D6", light: "rgba( 88,  86, 214, 0.10)" },
  { key: "purple",  name: "Purple",  dark: "#AF52DE", light: "rgba(175,  82, 222, 0.10)" },
  { key: "slate",   name: "Slate",   dark: "#B68800", light: "rgba(182, 145,   0, 0.10)" },
  { key: "rose",    name: "Rose",    dark: "#E43F1B", light: "rgba(228,  63,  27, 0.10)" },
  { key: "blue",    name: "Blue",    dark: "#007AFF", light: "rgba(  0, 122, 255, 0.10)" },
];

// Single source of truth for "no theme resolved yet" everywhere in the app
// (first-ever page load with nothing cached, an unrecognized cached key,
// and ThemedApp's AntD colorPrimary fallback on the pre-auth login/signup
// pages, which never have an org to resolve a real theme from). Must be
// "blue" (#007AFF) — not an arbitrary preset — since blue is this app's
// actual brand default; it previously defaulted to "orange", which is why
// a fresh load/hot-reload, and every antd-native hover/focus state on the
// login page (via colorPrimary), briefly or permanently showed orange
// regardless of the real org's theme.
export const DEFAULT_THEME_KEY = "blue";

// ── Helpers ──────────────────────────────────────────────────

/** Convert #RRGGBB → { r, g, b } */
export function hexToRgbObj(hex) {
  const clean = hex.replace("#", "");
  const bigint = parseInt(clean, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8)  & 255,
    b:  bigint        & 255,
  };
}

/** Convert #RRGGBB → "rgb(r, g, b)" */
export function hexToRgb(hex) {
  const { r, g, b } = hexToRgbObj(hex);
  return `rgb(${r}, ${g}, ${b})`;
}

/** Get theme object by key, fallback to default */
export function getThemeByKey(key) {
  return THEMES.find((t) => t.key === key) || THEMES.find((t) => t.key === DEFAULT_THEME_KEY);
}

// ── Apply ─────────────────────────────────────────────────────

// Applies a theme to the document — accepts either a theme key (string) or a theme object { dark }
export function applyTheme(themeOrKey) {
  const theme =
    typeof themeOrKey === "string"
      ? getThemeByKey(themeOrKey)
      : themeOrKey;

  if (!theme?.dark) return;

  const { r, g, b } = hexToRgbObj(theme.dark);
  const light = theme.light || `rgba(${r}, ${g}, ${b}, 0.10)`;

  const root = document.documentElement;
  root.style.setProperty("--colorDark",     theme.dark);
  root.style.setProperty("--colorDark-rgb", `${r}, ${g}, ${b}`);
  root.style.setProperty("--colorLight",    light);

  // ── Legacy aliases (keep existing styles working) ──
  root.style.setProperty("--primaryDark",       theme.dark);
  root.style.setProperty("--primaryLight",      light);
  root.style.setProperty("--primaryTextDark",   "#ffffff");
  root.style.setProperty("--primaryTextLight",  "#ffffff");
  root.style.setProperty("--secondaryDark",     theme.dark);
  root.style.setProperty("--secondaryLight",    light);
  root.style.setProperty("--secondaryTextDark", "#ffffff");
  root.style.setProperty("--secondaryTextLight","#ffffff");
}

// ── Persist / Load ────────────────────────────────────────────

const LS_KEY = "app-theme-key";
const ORG_LS_PREFIX = "app-theme-org-";

export function saveThemeKey(key) {
  try { localStorage.setItem(LS_KEY, key); } catch (_) {}
}

export function loadSavedThemeKey() {
  try { return localStorage.getItem(LS_KEY) || DEFAULT_THEME_KEY; } catch (_) { return DEFAULT_THEME_KEY; }
}

export function loadSavedTheme() {
  const key = loadSavedThemeKey();
  applyTheme(key);
  return key;
}

/** Save theme key for a specific org (CLIENT org users) */
export function saveOrgThemeKey(orgId, key) {
  try { localStorage.setItem(`${ORG_LS_PREFIX}${orgId}`, key); } catch (_) {}
}

/** Load theme key for a specific org, fallback to platform default */
export function loadOrgThemeKey(orgId) {
  try { return localStorage.getItem(`${ORG_LS_PREFIX}${orgId}`) || loadSavedThemeKey(); } catch (_) { return DEFAULT_THEME_KEY; }
}

// ── Favicon ───────────────────────────────────────────────────

export function updateFavicon(url) {
  let link = document.querySelector("link[rel*='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.type = "image/png";
  link.href = url;
}
